import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Divider,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from "@mui/material";
import { TbCalendarOff } from "react-icons/tb";
import { BiShow } from "react-icons/bi";
import { useGetRefundedSalesQuery } from "../../../redux/slices/admin/payment/paymentSlince.js";
import { useGetMyRefundedSalesQuery } from "../../../redux/slices/seller/payment/paymentSlince.js";
import { useOutletContext, useLocation } from "react-router-dom";
import { Pagination } from "@mui/material";
import "../../../styles/admin.css/payments-css/total-payments.css";

export default function RefundedSales({ isAdmin = false }) {
    const { sidebarOpen } = useOutletContext();
    const location = useLocation();
    const highlightId = location.state?.highlightId;
    
    const [page, setPage] = useState(1);
    const itemsPerPage = 50;

    const adminQueryResult = useGetRefundedSalesQuery(undefined, {
        skip: !isAdmin,
        refetchOnMountOrArgChange: true,
    });

    const sellerQueryResult = useGetMyRefundedSalesQuery(undefined, {
        skip: isAdmin,
        refetchOnMountOrArgChange: true,
    });

    const { data, isLoading } = isAdmin ? adminQueryResult : sellerQueryResult;
    const paymentsData = data?.data || [];

    useEffect(() => {
        if (highlightId) {
            const sale = paymentsData.find(s => s.id === highlightId);
            if (sale) {
                setOpenRow(sale.id);
                window.history.replaceState({}, document.title);
            }
        }
    }, [highlightId, paymentsData]);

    const [search, setSearch] = useState("");
    const [openRow, setOpenRow] = useState(null);

    const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
    const [customerFilter, setCustomerFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("");

    const formatCarPlate = (plate) => {
        if (!plate) return "";
        return plate.replace(/^(\d{2})([A-Z]+)(\d{3})$/, "$1-$2-$3");
    };

    const filteredPayments = paymentsData.filter(emp => {
        const methodMatch =
            paymentMethodFilter === "all" ||
            emp.paymentType === paymentMethodFilter;

        let customerMatch = true;
        if (customerFilter === "Qeydiyat nisani") {
            customerMatch = emp.isBuyCar === true;
        } else if (customerFilter === "Əl ilə alındı") {
            customerMatch = emp.isBuyCar === false;
        } else if (customerFilter === "Umico") {
            customerMatch = emp.isUmico === true;
        }

        let dateMatch = true;
        if (dateFilter) {
            const paymentDate = emp.createdAt ? new Date(emp.createdAt).toISOString().split('T')[0] : "";
            dateMatch = paymentDate === dateFilter;
        }

        const searchMatch =
            (emp.sellerName || "").toLowerCase().includes(search.toLowerCase()) ||
            (emp.carPlate || "").toLowerCase().includes(search.toLowerCase()) ||
            (emp.paymentType || "").toLowerCase().includes(search.toLowerCase());

        return methodMatch && customerMatch && dateMatch && searchMatch;
    });

    const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
    const startIndex = (page - 1) * itemsPerPage;
    const paginatedPayments = filteredPayments.slice(startIndex, startIndex + itemsPerPage);

    const handleShowClick = (emp) => {
        setOpenRow(openRow === emp.id ? null : emp.id);
    };

    return (
        <Box className={`total-payments-container ${sidebarOpen ? "sidebar-open" : ""}`}>
            <Box className="total-payments-header">
                <Box className="total-payments-title">
                    <Typography style={{ color: "#545A61" }}>Geri qaytarmalar</Typography>
                    <Divider className="total-payments-divider" />
                </Box>

                <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, gap: 10 }}>
                    <Box style={{ display: 'flex', gap: 10 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #ccc",
                                width: "40px",
                                height: "40px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)"
                                }
                            }}
                            onClick={() => setDateFilter("")}
                        >
                            <TbCalendarOff size={18} />
                        </Box>

                        <FormControl size="small">
                            <TextField
                                type="date"
                                size="small"
                                label="Tarix"
                                InputLabelProps={{ shrink: true }}
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                style={{ minWidth: 160 }}
                            />
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Ödəniş Üsulu</InputLabel>
                            <Select
                                value={paymentMethodFilter}
                                label="Ödəniş Üsulu"
                                onChange={(e) => { setPaymentMethodFilter(e.target.value); }}
                                style={{ minWidth: 140 }}
                            >
                                <MenuItem value="all">Bütün Qeydlər</MenuItem>
                                <MenuItem value="Kart">Kart</MenuItem>
                                <MenuItem value="Nağd">Nağd</MenuItem>
                                <MenuItem value="Kart və nağd">Kart və nağd</MenuItem>
                                <MenuItem value="POS-terminal">POS-terminal</MenuItem>
                                <MenuItem value="Nəğd/POS-terminal">Nəğd/POS-terminal</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Müştəri</InputLabel>
                            <Select
                                value={customerFilter}
                                label="Müştəri"
                                onChange={(e) => { setCustomerFilter(e.target.value); }}
                                style={{ minWidth: 140 }}
                            >
                                <MenuItem value="all">Bütün Qeydlər</MenuItem>
                                <MenuItem value="Qeydiyat nisani">Qeydiyat nisani</MenuItem>
                                <MenuItem value="Əl ilə alındı">Əl ilə alındı</MenuItem>
                                <MenuItem value="Umico">Umico</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField
                        size="small"
                        placeholder="Axtar..."
                        className="total-payments-search"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); }}
                    />
                </Box>
            </Box>

            <TableContainer component={Paper} className="total-payments-table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>#No</strong></TableCell>
                            <TableCell><strong>Ödəniş Üsulu</strong></TableCell>
                            <TableCell><strong>Müştəri</strong></TableCell>
                            <TableCell><strong>Satış Tarixi</strong></TableCell>
                            <TableCell><strong>Seçimlər</strong></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Yüklənir...</TableCell>
                            </TableRow>
                        ) : paginatedPayments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Məlumat tapılmadı</TableCell>
                            </TableRow>
                        ) : (
                            paginatedPayments.map((emp) => (
                                <React.Fragment key={emp.paymentId}>
                                    <TableRow className={highlightId === emp.id ? 'highlight-row' : ''}>
                                        <TableCell>{"#" + String(emp.serialNo || emp.id).padStart(4, "0")}</TableCell>
                                        <TableCell>{emp.paymentType}</TableCell>
                                        <TableCell>
                                            <strong>
                                                {emp.isBuyCar
                                                    ? formatCarPlate(emp.carPlate)
                                                    : emp.isUmico
                                                        ? "Umico"
                                                        : "Əl ilə alındı"}
                                            </strong>
                                        </TableCell>

                                        <TableCell>{emp.createdAt ? new Date(emp.createdAt).toLocaleString() : "-"}</TableCell>
                                        <TableCell className="manage-wrapper">
                                            <Button size="small" variant="contained" className="total-payments-btn" onClick={() => handleShowClick(emp)}>
                                                <BiShow style={{ width: "24px", height: "24px" }} />
                                            </Button>
                                        </TableCell>
                                    </TableRow>

                                    {openRow === emp.id && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="total-payments-expanded-cell">
                                                <Box className="payment-detailss">
                                                    <TableContainer component={Paper} style={{ boxShadow: "none", marginBottom: "20px" }}>
                                                        <Table size="small" className="payment-table">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell><strong>Məhsul</strong></TableCell>
                                                                    <TableCell><strong>Qiymət</strong></TableCell>
                                                                    <TableCell><strong>Endirim</strong></TableCell>
                                                                    <TableCell><strong>Maya</strong></TableCell>
                                                                    <TableCell><strong>Miqdar</strong></TableCell>
                                                                    <TableCell><strong>Kod</strong></TableCell>
                                                                    <TableCell><strong>Yanacaq Növü</strong></TableCell>
                                                                    <TableCell><strong>Ümumi Qiymət</strong></TableCell>
                                                                </TableRow>
                                                            </TableHead>

                                                            <TableBody>
                                                                {emp.items?.map((prod, idx) => {
                                                                    const isOil = prod.subcategoryName === "Motor yağı" || prod.subcategoryName === "Transmissiya yağı";

                                                                    return (
                                                                        <TableRow key={idx}>
                                                                            <TableCell>{prod.productName ?? prod.serviceName ?? '-'}</TableCell>
                                                                            <TableCell>
                                                                                {prod.sellPriceAtMoment != null
                                                                                    ? prod.sellPriceAtMoment
                                                                                    : '-'}₼
                                                                            </TableCell>

                                                                            <TableCell>
                                                                                {prod.discountAmount ? `${prod.discountAmount}₼` : '-'}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {prod.costPriceAtMoment ? `${prod.costPriceAtMoment}₼` : '-'}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {prod.productName == null
                                                                                    ? "Xidmət"
                                                                                    : `${prod.quantity} ${isOil ? "Litr" : "Ədəd"}`}
                                                                            </TableCell>
                                                                            <TableCell>{prod.code ? prod.code : "-"}</TableCell>
                                                                            <TableCell>{prod.fuelType ? prod.fuelType : "-"}</TableCell>
                                                                            <TableCell>{prod.totalPrice}₼</TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>

                                                    <TableContainer component={Paper} style={{ boxShadow: "none" }}>
                                                        <Table size="small" className="payment-table">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell><strong>Ödənildi</strong></TableCell>
                                                                    {emp.paymentType === "Kart və nağd" ? (
                                                                        <>
                                                                            <TableCell><strong>Kart</strong></TableCell>
                                                                            <TableCell><strong>Nağd</strong></TableCell>
                                                                        </>
                                                                    ) : emp.paymentType === "Nəğd/POS-terminal" ? (
                                                                        <>
                                                                            <TableCell><strong>POS-terminal</strong></TableCell>
                                                                            <TableCell><strong>Nağd</strong></TableCell>
                                                                        </>
                                                                    ) : (
                                                                        <TableCell><strong>{emp.paymentType}</strong></TableCell>
                                                                    )}
                                                                </TableRow>
                                                            </TableHead>

                                                            <TableBody>
                                                                <TableRow>
                                                                    <TableCell>{emp.totalSalePrice}₼</TableCell>
                                                                    {emp.paymentType === "Kart və nağd" ? (
                                                                        <>
                                                                            <TableCell>{emp.card}₼</TableCell>
                                                                            <TableCell>{emp.cash}₼</TableCell>
                                                                        </>
                                                                    ) : emp.paymentType === "Nəğd/POS-terminal" ? (
                                                                        <>
                                                                            <TableCell>{emp.card}₼</TableCell>
                                                                            <TableCell>{emp.cash}₼</TableCell>
                                                                        </>
                                                                    ) : (
                                                                        <TableCell>{(emp.paymentType === "Kart" || emp.paymentType === "POS-terminal") ? emp.card : emp.cash}₼</TableCell>
                                                                    )}
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {totalPages > 1 && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
}
